import {
  Box,
  Button,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { isQueryError } from 'lib-shared-auth';
import type { Conversation } from 'lib-shared-legacy-types';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import { AppLink } from 'components/appLink';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { ConversationEventGroups } from 'components/conversation/conversationEventGroups';
import { ConversationEventGroupsGroupConfig } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';
import { ConversationContextProvider } from 'components/conversation/conversationProvider';
import { ConversationSummaryByCsid } from 'components/conversation/conversationSummaryByCsid';
import { useCommandHandler } from 'utils/commands';
import { useReadPatchedConversation } from 'utils/conversation/useReadPatchedConversation';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { ConversationPreviewCopilotDropdown } from './components/copilotDropdown/conversationPreviewCopilotDropdown';
import { ConversationPreviewCustomFields } from './components/customFields/conversationPreviewCustomFields';
import { ConversationPreviewDetails } from './components/details/conversationPreviewDetails';
import { ConversationPreviewTags } from './components/tags/conversationPreviewTags';
import styles from './conversationPreview.module.scss';

/** The config for which event groups to present in the preview. */
const groupsConfig: readonly ConversationEventGroupsGroupConfig[] = [
  'group:pstn_phone',
  'group:voicemail',
  {
    'group:contact_form': { hideAttachments: true, hideGutterActions: true },
    'group:email': { hideAttachments: true, hideGutterActions: true },
    'group:messenger': { hideAttachments: true, hideGutterActions: true },
    'group:adachat': { hideAttachments: true, hideGutterActions: true },
    'group:digitalgeniuschat': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:kindlychat': { hideAttachments: true, hideGutterActions: true },
    'group:genericapimessaging': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:trustpilotreviews': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:yotporeviews': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:widget_chat': { hideAttachments: true, hideGutterActions: true },
    'group:facebook_messenger': {
      hideAttachments: true,
      hideGutterActions: true,
    },
    'group:whats_app': { hideAttachments: true, hideGutterActions: true },
    'group:sms': { hideAttachments: true, hideGutterActions: true },
    'group:twitter': { hideAttachments: true, hideGutterActions: true },
    'group:instagram_story': { hideGutterActions: true },
    'group:conversation:wrapup_message': { hideGutterActions: true },
  },
];

type ConversationPreviewProps = {
  /** The id of the conversation to preview. */
  csid: number;
  /** Called when navigating to the conversation. */
  onNavigateToConversation?: VoidFunction;
};

export const ConversationPreview = ({
  csid,
  onNavigateToConversation,
}: ConversationPreviewProps): JSX.Element => {
  const navigate = useAppNavigate();

  const [isFulfilled, setIsFulfilled] = useState(false);
  const [readConversation, { data: conversationData, error, asyncStatus }] =
    useReadPatchedConversation();

  const mainConversation = conversationData?.data.history.main_conversation;
  const isRejected = asyncStatus === 'rejected';
  const isForbidden = isRejected && isQueryError(error) && error.status === 403;

  useEffect(() => {
    setIsFulfilled(false);
    readConversation({ csid });
  }, [csid, readConversation]);

  useDebounce(
    () => {
      setIsFulfilled(asyncStatus === 'fulfilled');
    },
    300,
    [asyncStatus],
  );

  useCommandHandler({
    name: 'conversation:goToConversation',
    actionFn: () => {
      navigate({
        path: '/conversation/:csid',
        params: {
          path: {
            csid: `${csid}`,
          },
        },
      });

      onNavigateToConversation?.();
    },
  });

  if (isRejected) {
    return (
      <FlexBox
        justifyContent="center"
        alignItems="center"
        padding="500"
        height="100%"
      >
        <EmptyState
          title={isForbidden ? 'Access denied' : 'Unable to load conversation'}
          description={
            isForbidden
              ? 'You do not have access to view this conversation as it is in a restricted queue. Please consult your organization admin to gain access.'
              : 'Something went wrong while loading this conversation. Please try again later.'
          }
        />
      </FlexBox>
    );
  }

  if (!isFulfilled || !mainConversation) {
    return (
      <FlexBox
        justifyContent="center"
        alignItems="center"
        padding="500"
        height="100%"
      >
        <Spinner size={32} />
      </FlexBox>
    );
  }

  return (
    <ConversationContextProvider
      conversation={mainConversation as unknown as Conversation}
    >
      <Columns>
        <Column>
          <Column.Row noMinHeight>
            <FlexBox flexDirection="column" gap="300">
              <FlexBox
                alignItems="center"
                justifyContent="space-between"
                gap="200"
              >
                <ConversationPreviewDetails conversation={mainConversation} />

                <FlexBox alignItems="center" gap="200">
                  <ConversationPreviewCopilotDropdown />

                  <Tooltip
                    content={
                      <CommandKeybindingsTooltipContent commandName="conversation:goToConversation">
                        View conversation
                      </CommandKeybindingsTooltipContent>
                    }
                  >
                    <AppLink
                      path="/conversation/:csid"
                      params={{ path: { csid: `${csid}` } }}
                      onClick={onNavigateToConversation}
                      tabIndex={-1}
                    >
                      <Button variant="secondary">View conversation</Button>
                    </AppLink>
                  </Tooltip>
                </FlexBox>
              </FlexBox>

              {mainConversation.tags.length > 0 && (
                <ConversationPreviewTags
                  tags={mainConversation.tags}
                  showSectionIcon
                />
              )}

              <ConversationPreviewCustomFields
                csid={csid}
                showSectionIcon
                className={styles.customFields}
              />
            </FlexBox>
          </Column.Row>

          <Column.Row scrollable borderTop>
            <FlexBox flexDirection="column" gap="300">
              {mainConversation.summary.can_be_generated && (
                <ConversationSummaryByCsid
                  csid={mainConversation.id}
                  view="preview"
                />
              )}

              <ConversationEventGroups
                mainConversation={mainConversation}
                groupsConfig={groupsConfig}
                emptyMessage={
                  <Box padding="400 0">
                    <EmptyState
                      title="Nothing to preview"
                      description="There are no messages to preview in the conversation."
                    />
                  </Box>
                }
              />
            </FlexBox>
          </Column.Row>
        </Column>
      </Columns>
    </ConversationContextProvider>
  );
};
